import React, { useReducer } from 'react';
import PropTypes from 'prop-types';

import ActivityIcon from 'components/ActivityIcon';
import { Markup } from 'interweave';
import { isEmpty, snakeCase } from 'lodash-es';
import Button from '@strava/ui/Button';
import styles from './styles.scss';
import useFeedAnalytics from '../../hooks/useFeedAnalytics';
import PhotosAndMapImage from '../shared/PhotosAndMapImage';
import {
  pageProps,
  viewingAthleteProps,
  cursorDataProps,
  feedTypeProps,
  indexProps,
  timeAndLocationProps,
  mapProps,
  photoListProps
} from '../../props/feed-prop-types';
import FeedEntityContext from '../../state/FeedEntityContext';
import { init, reducer } from '../../state/feed-entity-reducer';
import { buildInitialState } from '../../state/feed-entity-init-state';
import LocationAndTime from './../shared/LocationAndTime';
import { getPhotoWithMaxDimension } from '../../utils';
import EntryHeader from '../shared/EntryHeader/EntryHeader';
import ActivityEntryBody from '../shared/ActivityEntryBody';
import MaharajAchievementCelebration from '../shared/MaharajAchievementCelebration';
import KudosAndComments from '../shared/KudosAndComments';
import CommentDeleteAlertDialog from '../shared/CommentDeleteAlertDialog';
import { OPEN_MODAL_OTHER_ATHLETES_TAB } from '../../state/feed-entity-actions';

const GroupActivity = ({
  viewingAthlete,
  clubId,
  page,
  entity,
  feedType,
  index,
  cursorData,
  rowData,
  athleteProfileId,
  timeAndLocation,
  switchMapAndPhoto,
  photos,
  kudosAndComments
}) => {
  const headLineActivity = rowData.activities[0];
  const headlineEntityId =
    headLineActivity.entity_id && headLineActivity.entity_id.toString();

  const analyticProps = {
    page,
    entity,
    athleteProfileId,
    entityId: headlineEntityId,
    activityIds: rowData.activities.map((a) => a.entity_id),
    feedType,
    cursorData,
    viewingAthleteId: viewingAthlete.id,
    clubId
  };

  const { trackAnalytics, intersectionObserverRef } = useFeedAnalytics({
    ...analyticProps
  });

  const commentsByActivityId = (() => {
    const commentsHash = {};
    if (!isEmpty(kudosAndComments)) {
      Object.entries(kudosAndComments).forEach((entry) => {
        commentsHash[entry[0].toString()] = entry[1].comments;
      });
    }
    return commentsHash;
  })();

  const [entityState, dispatch] = useReducer(
    reducer,
    buildInitialState({
      trackAnalytics,
      comments: commentsByActivityId
    }),
    init
  );

  const providerState = {
    entityState,
    dispatch,
    analyticProps
  };

  const cardMargin = index > 0 ? styles.cardMargin : '';

  const buildHeader = () => {
    const elements = [];
    // activity icon
    if (headLineActivity.activity_class_name) {
      elements.push(
        <div key="activity_icon" className={styles.mediaLeft}>
          <div className={styles.activityIcon}>
            <ActivityIcon
              data-testid="activity_icon"
              type={headLineActivity.activity_class_name.toLowerCase()}
              size="small"
              className={styles.activityIconWrapper}
            />
          </div>
        </div>
      );
    }
    // headline title, location and time
    elements.push(
      <div key="activity_entry_body" className={`${styles.mediaBody}`}>
        <Button
          variant="text"
          className={styles.headlineTextBtn}
          type="button"
          onClick={() =>
            dispatch({
              type: OPEN_MODAL_OTHER_ATHLETES_TAB,
              payload: {
                element: 'group_header',
                selectedEntityId: headlineEntityId
              }
            })
          }
        >
          <Markup noWrap={true} content={rowData.title} noHtml={true} />
        </Button>

        <LocationAndTime
          displayDate={timeAndLocation.displayDate}
          displayDateAtTime={timeAndLocation.displayDateAtTime}
          location={timeAndLocation.location}
          timestampFormat={timeAndLocation.timestampFormat}
        />
      </div>
    );
    return elements;
  };

  const headlineBannerProps = () => {
    let bannerImage;
    if (switchMapAndPhoto) {
      // replace activity map with a photo
      const headlinePhoto = headLineActivity.photos[0];
      if (headlinePhoto) {
        const photoUrl = getPhotoWithMaxDimension(headlinePhoto.urls);
        bannerImage = { url: photoUrl, alt: headlinePhoto.caption };
      } else {
        // fallback (this scenario should ideally not exist in prod)
        bannerImage = { url: '', alt: '' };
      }
    } else {
      bannerImage = rowData.map;
    }
    return {
      activityId: headLineActivity.entity_id.toString(),
      activityMap: bannerImage,
      entryType: snakeCase(entity),
      isRoutable: headLineActivity.is_routable,
      feedName: page,
      isVirtual: headLineActivity.is_virtual,
      isCommute: headLineActivity.is_commute,
      workoutType: headLineActivity.workout_type
    };
  };

  const entryBodyProps = (activity) => {
    return {
      stats: activity.stats,
      description: activity.description,
      flagged: activity.flagged,
      activityName: activity.name,
      activityId: activity.entity_id.toString(),
      ownedByCurrentAthlete: activity.owned_by_current_athlete,
      segAndBestEffortAchievements: activity.achievements,
      powerAndSegmentGoalAchievements: activity.performance_goals,
      cumulativeGoalAchievement: activity.goals,
      activityType: activity.type,
      athleteSex: activity.athlete_sex,
      athleteFirstName: activity.athlete_firstname
    };
  };

  const buildListEntries = () => {
    return rowData.activities.map((activity) => {
      const entityOwner = {
        displayImage: activity.athlete_avatar_url,
        name: activity.athlete_name,
        memberType: activity.member_type,
        profileUrl: `/athletes/${activity.athlete_id}`,
        type: 'athlete',
        id: activity.athlete_id.toString()
      };
      const noAchievementsAndMapsPhotos =
        isEmpty(activity.web_achievement) &&
        (photos && isEmpty(photos[activity.entity_id]));
      return (
        <li className={styles.childEntry} key={activity.entity_id}>
          {/* Entry Header - avatar, social actions */}
          <EntryHeader
            entityOwner={entityOwner}
            entityId={activity.entity_id.toString()}
            isBoosted={activity.is_boosted || false}
            shareable={activity.shareable || false}
            twitterUrl={activity.twitter_url}
            entity={entity}
          />

          {/* Entry Body =  activity title, description, stats and achievements  */}
          <div className={`${styles.entryBody} ${styles.entryInset}`}>
            <ActivityEntryBody {...entryBodyProps(activity)} />
          </div>

          {/* Maharaj achievements */}
          {!isEmpty(activity.web_achievement) && (
            <div className={`${styles.entryMedia} ${styles.entryInset}`}>
              <MaharajAchievementCelebration
                image={activity.web_achievement.image}
                image_caption={activity.web_achievement.image_caption}
                subtitle={activity.web_achievement.subtitle}
                text={activity.web_achievement.text}
                url={activity.web_achievement.url}
              />
            </div>
          )}

          {/* Map and Photos */}
          {photos && !isEmpty(photos[activity.entity_id]) && (
            <div className={`${styles.entryMedia} ${styles.entryInset}`}>
              <PhotosAndMapImage
                activityId={activity.entity_id.toString()}
                entryType={snakeCase(entity)}
                photoList={photos[activity.entity_id].photoList}
                feedName={page}
              />
            </div>
          )}

          {/* Kudos And Comments */}
          <div
            className={`${styles.entryFooter} ${styles.entryInset} ${
              noAchievementsAndMapsPhotos
                ? styles.noAchievementsAndMapsPhotos
                : ''
            }`}
          >
            <KudosAndComments
              kudosAndComments={kudosAndComments[activity.entity_id]}
              entity={snakeCase(entity)}
              entityId={activity.entity_id.toString()}
              ownedByCurrentAthlete={activity.owned_by_current_athlete}
              viewingAthlete={viewingAthlete}
              entityOwner={entityOwner}
              entityTitle={activity.name}
              openCommentsModalOnIconClick={true}
              segAndBestEffortAchievements={activity.achievements}
              showHairlineOnTop={false}
            />
          </div>
        </li>
      );
    });
  };

  return (
    <FeedEntityContext.Provider value={providerState}>
      {/* web-feed-4-entry is being used by feed3 container for pagination - https://github.com/strava/active/blob/main/app/assets/javascripts/strava/dashboard/PaginationView.js.coffee#L52-L73 */}
      <div
        className={`web-feed-4-entry ${styles.feedEntry} ${styles.card} ${cardMargin}`}
        data-updated-at={cursorData.updated_at}
        data-rank={cursorData.rank}
        data-testid="web-feed-entry"
        ref={intersectionObserverRef}
      >
        {/* headline title, activity icon, time and location */}
        <div className={styles.entryHeader}>
          <div className={`${styles.media} ${styles.mediaMiddle}`}>
            {buildHeader()}
          </div>
        </div>

        {/* heading map or photo */}
        <div className={`${styles.entryMedia} ${styles.headlineImage}`}>
          <PhotosAndMapImage {...headlineBannerProps()} />
        </div>

        {/* individual athlete activity entries */}
        <div className={styles.entryBlock}>
          <ul className={styles.listEntries}>{buildListEntries()}</ul>
        </div>
        <CommentDeleteAlertDialog />
      </div>
    </FeedEntityContext.Provider>
  );
};

GroupActivity.propTypes = {
  // feed entity could be Activity, group_activity, Post etc
  entity: PropTypes.oneOf(['GroupActivity']).isRequired,
  viewingAthlete: viewingAthleteProps.isRequired,
  clubId: PropTypes.string.isRequired,
  page: pageProps.isRequired,
  cursorData: cursorDataProps.isRequired,
  feedType: feedTypeProps.isRequired,
  // range of number 0...19 representing rows returned by the server per feed load
  index: indexProps.isRequired,
  rowData: PropTypes.shape({
    title: PropTypes.string.isRequired,
    map: mapProps,
    activities: PropTypes.arrayOf(
      PropTypes.shape({
        activity_class_name: PropTypes.string,
        entity_id: PropTypes.number,
        activity_map: mapProps,
        is_routable: PropTypes.bool,
        is_commute: PropTypes.bool,
        is_virtual: PropTypes.bool,
        workout_type: PropTypes.string,
        photos: PropTypes.arrayOf(
          PropTypes.shape({
            urls: PropTypes.shape({}).isRequired,
            caption: PropTypes.string
          })
        )
      })
    )
  }).isRequired,
  photos: PropTypes.shape({
    photoList: photoListProps
  }).isRequired,
  timeAndLocation: timeAndLocationProps.isRequired,
  athleteProfileId: PropTypes.string.isRequired,
  switchMapAndPhoto: PropTypes.bool.isRequired,
  kudosAndComments: PropTypes.shape({}).isRequired
};

export default GroupActivity;
